import { Link } from "@inertiajs/react"
import ProductLink from "@/components/productQuickView/ProductLink"
import { formatMoneyShort } from "@/lib/money"
import SortableHead from "../../Revenue/components/SortableHead"
import type { SortState } from "../../Revenue/sort"
import { AttainmentLine } from "../BottomUp/components/ActualsCell"
import UnplannedNote, { type UnplannedChannel, type UnplannedLink, type UnplannedProduct } from "./UnplannedNote"
import YearTargetBar from "./YearTargetBar"

export type RowKind = "products" | "categories" | "channels"

const NAME_HEADING: Record<RowKind, string> = { products: "Product", categories: "Category", channels: "Channel" }

export interface ActualsFigures {
  actual: number
  plan: number | null // null: no product here has a plan
  // The actual revenue the plan is compared with: products with a plan only.
  planned_actual: number
  // Products that sold with no plan; their sales are left out of % of plan.
  unplanned: UnplannedProduct[]
  // Actual GM as a fraction; absent without the gross margin permission.
  gm?: number | null
  // Only for the whole plan period.
  target?: number
}

export interface ActualsRow extends ActualsFigures {
  id: string
  name: string
  // Categories and channels: where clicking the row goes.
  slug?: string
  // Products only.
  full_name?: string | null
  sku?: string | null
  plan_node?: string | null
  units?: number
}

interface Props {
  rowKind: RowKind
  rows: ActualsRow[]
  total: ActualsFigures
  currency: string | null
  showGm: boolean
  emptyText: string
  // Categories and channels: the drill-down link for a row, or null to keep its name plain.
  hrefForRow?: (row: ActualsRow) => string | null
  linkForChannel: (channel: UnplannedChannel) => UnplannedLink | null
  sort: SortState
  onSort: (key: string) => void
}

const CELL = "whitespace-nowrap px-4 py-2.5 text-right tabular-nums"
const HEAD = "h-11 whitespace-nowrap px-4 align-middle text-[13px] font-medium text-muted-foreground"
const TOTAL_BG = { background: "color-mix(in srgb, var(--muted) 50%, var(--card))" }

// A ₫0 plan is a real plan: any sale is over it.
function PlanProgress({ actual, plan }: { actual: number; plan: number | null }) {
  if (plan == null) return null
  if (plan > 0) return <AttainmentLine actual={actual} plan={plan} />
  return actual > 0 ? <span>Over plan</span> : null
}

function Line({ row, rowKind, currency, showGm, showTarget, linkForChannel, href = null, isTotal = false }: {
  row: ActualsRow; rowKind: RowKind; currency: string | null; showGm: boolean; showTarget: boolean
  linkForChannel: (channel: UnplannedChannel) => UnplannedLink | null; href?: string | null; isTotal?: boolean
}) {
  const strong = isTotal ? "font-semibold" : ""
  // A product row is one product: its "No plan" opens the channels missing it.
  const productRow = rowKind === "products" && !isTotal
  return (
    <tr className="border-b" style={isTotal ? TOTAL_BG : undefined}>
      <td className={`px-4 py-2.5 text-left ${strong}`} title={row.full_name ?? undefined}>
        <div>
          {productRow ? (
            // A product row's id is the product's external id.
            <ProductLink externalId={row.id}>{row.name}</ProductLink>
          ) : href ? (
            <Link href={href} className="text-link hover:underline">{row.name}</Link>
          ) : (
            row.name
          )}
        </div>
        {row.plan_node && <div className="text-xs text-muted-foreground">{row.plan_node}</div>}
      </td>
      <td className={`${CELL} ${strong}`}>
        <div>{formatMoneyShort(row.actual, currency)}</div>
        {!productRow && <UnplannedNote products={row.unplanned} currency={currency} linkForChannel={linkForChannel} />}
      </td>
      <td className={CELL}>
        {row.plan != null ? formatMoneyShort(row.plan, currency)
          : productRow && row.unplanned.length > 0
            ? <UnplannedNote products={row.unplanned} currency={currency} linkForChannel={linkForChannel} label="No plan" />
            : <span className="text-xs">No plan</span>}
      </td>
      <td className="w-40 px-4 py-2.5 text-xs">
        <PlanProgress actual={row.planned_actual} plan={row.plan} />
      </td>
      {showGm && <td className={CELL}>{row.gm == null ? "" : `${(row.gm * 100).toFixed(1)}%`}</td>}
      {rowKind === "products" && (
        <td className={CELL}>{row.units == null ? "" : row.units.toLocaleString("en-US")}</td>
      )}
      {showTarget && (
        <td className={`${CELL} text-xs`}>
          {row.target != null && row.target > 0 && <YearTargetBar actual={row.actual} target={row.target} currency={currency} />}
        </td>
      )}
    </tr>
  )
}

// One line per product, category or channel, with a Total line on top.
// Products show units sold. The year target shows only for the whole plan period.
// Actual revenue, GM% and units sort on a heading click; Total stays on top.
export default function ActualsTable({ rowKind, rows, total, currency, showGm, emptyText, hrefForRow, linkForChannel, sort, onSort }: Props) {
  const showTarget = total.target != null
  const columns = 4 + (showGm ? 1 : 0) + (rowKind === "products" ? 1 : 0) + (showTarget ? 1 : 0)
  const shared = { rowKind, currency, showGm, showTarget, linkForChannel }
  const sortable = (key: string) => ({ sortKey: key, active: sort.sort === key ? sort.dir : null, onSort, align: "right" as const, className: `${HEAD} text-right` })
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b">
          <th className={`${HEAD} text-left`}>{NAME_HEADING[rowKind]}</th>
          <SortableHead {...sortable("actual")}>Actual revenue</SortableHead>
          <th className={`${HEAD} text-right`}>Plan revenue</th>
          <th className={`${HEAD} text-left`}>% of plan</th>
          {showGm && <SortableHead {...sortable("gm")}>GM% (actual)</SortableHead>}
          {rowKind === "products" && <SortableHead {...sortable("units")}>Units sold</SortableHead>}
          {showTarget && <th className={`${HEAD} text-left`}>Year target</th>}
        </tr>
      </thead>
      <tbody>
        <Line row={{ ...total, id: "total", name: "Total" }} {...shared} isTotal />
        {rows.map((r) => (
          <Line key={r.id} row={r} {...shared} href={hrefForRow?.(r) ?? null} />
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns} className="px-4 py-8 text-center text-sm">{emptyText}</td>
          </tr>
        )}
      </tbody>
    </table>
  )
}
